import type { DriveStep } from 'driver.js';

export type TourRoleFlags = {
  isAdmin: boolean;
  isPlatformAdmin: boolean;
};

/** Resolve a visible element for responsive shells (desktop nav vs bottom nav). */
function pickElement(desktopSelector: string, mobileSelector: string, fallback: string): Element {
  const isDesktop =
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
  const primary = isDesktop ? desktopSelector : mobileSelector;
  return (document.querySelector(primary) ??
    document.querySelector(isDesktop ? mobileSelector : desktopSelector) ??
    document.querySelector(fallback)) as Element;
}

/**
 * Pure step list for the product tour. Element resolvers run when each step is shown.
 */
export function buildProductTourSteps(flags: TourRoleFlags): DriveStep[] {
  const { isAdmin, isPlatformAdmin } = flags;

  const steps: DriveStep[] = [
    {
      element: () => pickElement('[data-tour="shell-brand"]', '[data-tour="shell-brand"]', 'body'),
      popover: {
        title: 'Welcome to akoreps',
        description:
          'A quick tour of the main areas. Use Next or Done to move through—skip anytime with the close button.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: () =>
        pickElement('[data-tour="desktop-nav-dashboard"]', '[data-tour="mobile-nav-home"]', 'body'),
      popover: {
        title: 'Dashboard',
        description: 'Your home base for stats and shortcuts after each visit.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: () =>
        pickElement(
          '[data-tour="desktop-nav-practice"]',
          '[data-tour="mobile-nav-sessions"]',
          'body',
        ),
      popover: {
        title: 'Practice',
        description: 'Browse sessions and start new practice runs with your team.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: () =>
        pickElement(
          '[data-tour="desktop-nav-personas"]',
          '[data-tour="mobile-nav-personas"]',
          'body',
        ),
      popover: {
        title: 'Personas',
        description: 'Enable buyer personas and generate AI personas from your business profile.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: () =>
        pickElement('[data-tour="account-menu"]', '[data-tour="account-menu"]', 'body'),
      popover: {
        title: 'Account menu',
        description:
          'Open here to see your workspace, switch organizations when you belong to more than one, open Profile, or sign out. On mobile it sits next to the main menu button.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];

  if (isAdmin) {
    const mobile =
      typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches;
    if (mobile) {
      steps.push({
        element: () =>
          (document.querySelector('[data-tour="mobile-nav-more"]') as Element) ?? document.body,
        popover: {
          title: 'Admin & settings',
          description: isPlatformAdmin
            ? 'Tap More to open the menu—Business profile, Goals, Settings, and Platform admin live there on mobile.'
            : 'Tap More to open the menu—Business profile, Goals, and Settings live there on mobile.',
          side: 'top',
          align: 'center',
        },
      });
    } else {
      steps.push(
        {
          element: () => pickElement('[data-tour="desktop-nav-business-profile"]', 'body', 'body'),
          popover: {
            title: 'Business profile',
            description:
              'Capture positioning and ICP so practice and personas match your business.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: () => pickElement('[data-tour="desktop-nav-goals"]', 'body', 'body'),
          popover: {
            title: 'Goals',
            description: 'Define practice goals for your organization.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: () => pickElement('[data-tour="desktop-nav-settings"]', 'body', 'body'),
          popover: {
            title: 'Settings',
            description: 'Manage members, billing, and org-wide options.',
            side: 'bottom',
            align: 'start',
          },
        },
      );
    }
  }

  if (isPlatformAdmin) {
    const mobile =
      typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches;
    // Org admins on mobile already use the "More" step above; include Platform admin in that copy.
    if (!(isAdmin && mobile)) {
      steps.push({
        element: () => {
          if (mobile) {
            return (
              (document.querySelector('[data-tour="mobile-nav-more"]') as Element) ?? document.body
            );
          }
          return pickElement('[data-tour="desktop-nav-admin"]', 'body', 'body');
        },
        popover: {
          title: 'Platform admin',
          description: mobile
            ? 'Open More, then Platform admin for internal tools.'
            : 'Platform-wide tools for tenants and configuration.',
          side: mobile ? 'top' : 'bottom',
          align: 'start',
        },
      });
    }
  }

  return steps;
}
